/**
 * Hook for Argentina Bio Template with Province Management
 * Manages province-specific activation and template configuration
 */

import { ARGENTINA_PROVINCES } from "@/lib/argentinaProvinceDetection";
import { useState, useEffect, useCallback } from "react";

export interface ProvinceConfig {
  code: string;
  name: string;
  active: boolean;
  launchDate?: string;
  population: number;
  timezone: string;
}

export interface ArgentinaBioTemplateConfig {
  country: string;
  enabled: boolean;
  provinces: ProvinceConfig[];
  defaultProvince: string;
  maintenanceMessage: string;
  version: string;
}

/**
 * Default configuration for Argentina Bio Template
 */
const DEFAULT_ARGENTINA_CONFIG: ArgentinaBioTemplateConfig = {
  country: "AR",
  enabled: true,
  defaultProvince: "BA", // Buenos Aires as default
  maintenanceMessage: "Próximamente disponible en esta provincia",
  version: "1.0.0",
  provinces: Object.entries(ARGENTINA_PROVINCES).map(([code, province]) => ({
    code,
    name: province.name,
    active: code === "BA", // Only Buenos Aires active by default
    population: province.population,
    timezone: province.timezone,
  })),
};

// Local storage key
const ARGENTINA_CONFIG_KEY = "argentina_bio_template_config";
const CONFIG_VERSION_KEY = "argentina_bio_template_version";

/**
 * Hook for managing Argentina Bio Template configuration
 */
export function useArgentinaBioTemplate() {
  const [config, setConfig] = useState<ArgentinaBioTemplateConfig>(DEFAULT_ARGENTINA_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load configuration from localStorage or use defaults
  useEffect(() => {
    try {
      const loadConfig = () => {
        const savedConfig = localStorage.getItem(ARGENTINA_CONFIG_KEY);
        const savedVersion = localStorage.getItem(CONFIG_VERSION_KEY);

        if (savedConfig && savedVersion === DEFAULT_ARGENTINA_CONFIG.version) {
          // Load saved configuration
          const parsedConfig = JSON.parse(savedConfig) as ArgentinaBioTemplateConfig;

          // Validate configuration structure
          if (isValidConfig(parsedConfig)) {
            setConfig(parsedConfig);
            if (import.meta.env.DEV) {
            }
          } else {
            // Configuration is invalid, use defaults
            setConfig(DEFAULT_ARGENTINA_CONFIG);
            saveConfig(DEFAULT_ARGENTINA_CONFIG);
          }
        } else {
          // No saved config or version mismatch, use defaults
          setConfig(DEFAULT_ARGENTINA_CONFIG);
          saveConfig(DEFAULT_ARGENTINA_CONFIG);

          if (import.meta.env.DEV) {
          }
        }
      };

      loadConfig();
    } catch (err) {
      setError("Error loading configuration");
      setConfig(DEFAULT_ARGENTINA_CONFIG);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save configuration to localStorage
  const saveConfig = useCallback((newConfig: ArgentinaBioTemplateConfig) => {
    try {
      localStorage.setItem(ARGENTINA_CONFIG_KEY, JSON.stringify(newConfig));
      localStorage.setItem(CONFIG_VERSION_KEY, newConfig.version);
      if (import.meta.env.DEV) {
      }
    } catch (err) {
    }
  }, []);

  // Validate configuration structure
  const isValidConfig = (config: any): config is ArgentinaBioTemplateConfig => {
    return (
      config &&
      typeof config === "object" &&
      config.country === "AR" &&
      Array.isArray(config.provinces) &&
      config.provinces.every((p: any) =>
        p &&
        typeof p === "object" &&
        typeof p.code === "string" &&
        typeof p.name === "string" &&
        typeof p.active === "boolean"
      )
    );
  };

  // Check if a province is active
  const isProvinceActive = useCallback((provinceCode: string | null): boolean => {
    if (!provinceCode) return false;

    const province = config.provinces.find(p => p.code === provinceCode);
    return province ? province.active : false;
  }, [config.provinces]);

  // Get active provinces
  const getActiveProvinces = useCallback((): ProvinceConfig[] => {
    return config.provinces.filter(p => p.active);
  }, [config.provinces]);

  // Get inactive provinces
  const getInactiveProvinces = useCallback((): ProvinceConfig[] => {
    return config.provinces.filter(p => !p.active);
  }, [config.provinces]);

  // Activate a province
  const activateProvince = useCallback((provinceCode: string) => {
    setConfig(prevConfig => {
      const newConfig = {
        ...prevConfig,
        provinces: prevConfig.provinces.map(p =>
          p.code === provinceCode
            ? { ...p, active: true, launchDate: new Date().toISOString() }
            : p
        )
      };

      saveConfig(newConfig);
      return newConfig;
    });

    if (import.meta.env.DEV) {
    }
  }, [saveConfig]);

  // Deactivate a province
  const deactivateProvince = useCallback((provinceCode: string) => {
    setConfig(prevConfig => {
      const newConfig = {
        ...prevConfig,
        provinces: prevConfig.provinces.map(p =>
          p.code === provinceCode
            ? { ...p, active: false }
            : p
        )
      };

      saveConfig(newConfig);
      return newConfig;
    });

    if (import.meta.env.DEV) {
    }
  }, [saveConfig]);

  // Toggle province activation
  const toggleProvince = useCallback((provinceCode: string) => {
    const province = config.provinces.find(p => p.code === provinceCode);
    if (province) {
      if (province.active) {
        deactivateProvince(provinceCode);
      } else {
        activateProvince(provinceCode);
      }
    }
  }, [config.provinces, activateProvince, deactivateProvince]);

  // Activate multiple provinces
  const activateProvinces = useCallback((provinceCodes: string[]) => {
    setConfig(prevConfig => {
      const newConfig = {
        ...prevConfig,
        provinces: prevConfig.provinces.map(p =>
          provinceCodes.includes(p.code)
            ? { ...p, active: true, launchDate: p.launchDate || new Date().toISOString() }
            : p
        )
      };

      saveConfig(newConfig);
      return newConfig;
    });

    if (import.meta.env.DEV) {
    }
  }, [saveConfig]);

  // Deactivate multiple provinces
  const deactivateProvinces = useCallback((provinceCodes: string[]) => {
    setConfig(prevConfig => {
      const newConfig = {
        ...prevConfig,
        provinces: prevConfig.provinces.map(p =>
          provinceCodes.includes(p.code)
            ? { ...p, active: false }
            : p
        )
      };

      saveConfig(newConfig);
      return newConfig;
    });

    if (import.meta.env.DEV) {
    }
  }, [saveConfig]);

  // Reset configuration to defaults
  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_ARGENTINA_CONFIG);
    saveConfig(DEFAULT_ARGENTINA_CONFIG);

    if (import.meta.env.DEV) {
    }
  }, [saveConfig]);

  // Get province statistics
  const getProvinceStats = useCallback(() => {
    const activeCount = config.provinces.filter(p => p.active).length;
    const inactiveCount = config.provinces.filter(p => !p.active).length;
    const totalPopulation = config.provinces.reduce((sum, p) => sum + p.population, 0);
    const activePopulation = config.provinces
      .filter(p => p.active)
      .reduce((sum, p) => sum + p.population, 0);

    return {
      total: config.provinces.length,
      active: activeCount,
      inactive: inactiveCount,
      activePercentage: Math.round((activeCount / config.provinces.length) * 100),
      totalPopulation,
      activePopulation,
      activePopulationPercentage: Math.round((activePopulation / totalPopulation) * 100),
    };
  }, [config.provinces]);

  // Export configuration
  const exportConfig = useCallback(() => {
    return JSON.stringify(config, null, 2);
  }, [config]);

  // Import configuration
  const importConfig = useCallback((configJson: string) => {
    try {
      const importedConfig = JSON.parse(configJson);

      if (isValidConfig(importedConfig)) {
        setConfig(importedConfig);
        saveConfig(importedConfig);

        if (import.meta.env.DEV) {
        }

        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  }, [saveConfig]);

  return {
    // Core config
    config,
    isLoading,
    error,

    // Province management
    isProvinceActive,
    getActiveProvinces,
    getInactiveProvinces,
    activateProvince,
    deactivateProvince,
    toggleProvince,
    activateProvinces,
    deactivateProvinces,

    // Configuration management
    resetToDefaults,
    getProvinceStats,
    exportConfig,
    importConfig,

    // Utility functions
    templateEnabled: config.enabled,
    defaultProvince: config.defaultProvince,
    maintenanceMessage: config.maintenanceMessage,
  };
}